/**
 * Multi-window restaurant hours.
 * Functions keep a mirror in functions/src/businessHours.ts — update both.
 */
import type { BusinessHours, BusinessHoursDay, BusinessHoursPeriod, PublicOrderingDay, Weekday } from '../types';

export const WEEKDAY_ORDER: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const MAX_BUSINESS_HOUR_PERIODS = 6;

const WEEKDAY_SET = new Set<string>(WEEKDAY_ORDER);

export function parseTimeToMinutes(t: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

export function formatMinutesAsHHmm(mins: number): string {
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function dayLabel(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

function formatClockLabel(hhmm: string): string {
  const mins = parseTimeToMinutes(hhmm);
  if (mins == null) return hhmm;
  const hour24 = Math.floor(mins / 60);
  const minute = mins % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

/** Open windows for a day. A missing `periods` list falls back to the single open/close pair. */
export function resolveDayPeriods(day: {
  open?: string | null;
  close?: string | null;
  closed?: boolean;
  periods?: BusinessHoursPeriod[] | null;
}): BusinessHoursPeriod[] {
  if (day.closed) return [];
  const raw = Array.isArray(day.periods) ? day.periods : [];
  const fromList = raw
    .map((period) => ({
      open: typeof period?.open === 'string' ? period.open.trim() : '',
      close: typeof period?.close === 'string' ? period.close.trim() : '',
    }))
    .filter((period) => period.open && period.close);
  if (fromList.length > 0) {
    return fromList
      .slice()
      .sort((a, b) => (parseTimeToMinutes(a.open) ?? 0) - (parseTimeToMinutes(b.open) ?? 0));
  }
  const open = day.open?.trim();
  const close = day.close?.trim();
  if (open && close) return [{ open, close }];
  return [];
}

export function envelopeFromPeriods(periods: BusinessHoursPeriod[]): { open: string | null; close: string | null } {
  let minOpen = Infinity;
  let maxClose = -Infinity;
  for (const period of periods) {
    const open = parseTimeToMinutes(period.open);
    const close = parseTimeToMinutes(period.close);
    if (open == null || close == null) continue;
    if (open < minOpen) minOpen = open;
    if (close > maxClose) maxClose = close;
  }
  if (!Number.isFinite(minOpen) || maxClose < 0) return { open: null, close: null };
  return { open: formatMinutesAsHHmm(minOpen), close: formatMinutesAsHHmm(maxClose) };
}

export function normalizeBusinessHours(hours: BusinessHours): BusinessHours {
  const days = WEEKDAY_ORDER.map((day) => {
    const existing =
      hours.days.find((row) => row.day === day) ||
      ({ day, open: null, close: null, closed: true } as BusinessHoursDay);
    if (existing.closed) {
      return {
        ...existing,
        day,
        closed: true,
        open: null,
        close: null,
        periods: [] as BusinessHoursPeriod[],
      };
    }
    const periods = resolveDayPeriods(existing);
    const envelope = envelopeFromPeriods(periods);
    return {
      ...existing,
      day,
      closed: false,
      periods,
      open: envelope.open,
      close: envelope.close,
    };
  });
  return {
    timezone: hours.timezone?.trim() || 'Pacific/Auckland',
    days,
  };
}

/** Error message, or null when the week is valid. Incomplete rows are rejected. */
export function validateBusinessHours(hours: BusinessHours): string | null {
  for (const day of hours.days) {
    if (day.closed) continue;
    const label = dayLabel(day.day);
    const raw = Array.isArray(day.periods) && day.periods.length > 0 ? day.periods : resolveDayPeriods(day);
    if (raw.length === 0) {
      return `${label} needs at least one open and close time, or mark the day closed.`;
    }
    if (raw.length > MAX_BUSINESS_HOUR_PERIODS) {
      return `${label} can have at most ${MAX_BUSINESS_HOUR_PERIODS} open windows.`;
    }
    const parsed: { open: number; close: number }[] = [];
    for (const period of raw) {
      if (!period.open?.trim() || !period.close?.trim()) {
        return `${label} has an incomplete open or close time.`;
      }
      const open = parseTimeToMinutes(period.open);
      const close = parseTimeToMinutes(period.close);
      if (open == null || close == null) {
        return `${label} has a time that is not HH:mm.`;
      }
      if (close <= open) {
        return `${label}: close time ${formatClockLabel(period.close)} must be after open time ${formatClockLabel(period.open)}.`;
      }
      parsed.push({ open, close });
    }
    parsed.sort((a, b) => a.open - b.open);
    for (let i = 1; i < parsed.length; i++) {
      if (parsed[i].open < parsed[i - 1].close) {
        return `${label} has overlapping open windows.`;
      }
    }
  }
  return null;
}

export function publicOrderingSchedule(
  hours: BusinessHours | null | undefined
): { timezone: string; days: PublicOrderingDay[] } | null {
  if (!hours?.days?.length) return null;
  const normalized = normalizeBusinessHours(hours);
  return {
    timezone: normalized.timezone,
    days: normalized.days.map((day) => ({
      day: day.day,
      closed: day.closed === true,
      periods: day.closed ? [] : resolveDayPeriods(day),
    })),
  };
}

export function minutesInTimeZone(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  let hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);
  if (hour === 24) hour = 0;
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0;
  return hour * 60 + minute;
}

export function weekdayInTimeZone(date: Date, timeZone: string): Weekday {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' })
    .format(date)
    .toLowerCase();
  if (WEEKDAY_SET.has(name)) return name as Weekday;
  return 'monday';
}

export function dateKeyInTimeZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Weekday of a civil YYYY-MM-DD date (the date key itself, not a timezone conversion). */
export function weekdayFromDateKey(dateKey: string): Weekday | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const utc = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  const name = utc.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }).toLowerCase();
  if (WEEKDAY_SET.has(name)) return name as Weekday;
  return null;
}

export function isMinutesInPeriods(
  mins: number,
  periods: BusinessHoursPeriod[],
  inclusiveEnd: boolean
): boolean {
  for (const period of periods) {
    const open = parseTimeToMinutes(period.open);
    const close = parseTimeToMinutes(period.close);
    if (open == null || close == null || close <= open) continue;
    if (mins >= open && (inclusiveEnd ? mins <= close : mins < close)) return true;
  }
  return false;
}

function ceilToStep(mins: number, step: number): number {
  return Math.ceil(mins / step) * step;
}

/** 15-minute pickup times on the same civil day, inside open windows only. */
export function listPickupHHmm(
  now: Date,
  periods: BusinessHoursPeriod[],
  timeZone: string,
  leadMinutes = 30,
  stepMinutes = 15
): string[] {
  const lead = new Date(now.getTime() + leadMinutes * 60 * 1000);
  if (dateKeyInTimeZone(now, timeZone) !== dateKeyInTimeZone(lead, timeZone)) return [];
  const leadMin = ceilToStep(minutesInTimeZone(lead, timeZone), stepMinutes);
  if (leadMin >= 24 * 60) return [];
  const sorted = resolveDayPeriods({ closed: false, periods });
  const out: string[] = [];
  for (const period of sorted) {
    const open = parseTimeToMinutes(period.open);
    const close = parseTimeToMinutes(period.close);
    if (open == null || close == null || close <= open) continue;
    let slot = ceilToStep(open, stepMinutes);
    if (slot < leadMin) slot = leadMin;
    for (; slot <= close; slot += stepMinutes) {
      if (slot >= open) out.push(formatMinutesAsHHmm(slot));
    }
  }
  return out;
}

/** Absolute instant for today's HH:mm in `timeZone`. */
export function zonedTodayAtMinutes(now: Date, minutes: number, timeZone: string): Date {
  const dateKey = dateKeyInTimeZone(now, timeZone);
  const [year, month, day] = dateKey.split('-').map(Number);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  let utc = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 4; i++) {
    const got = new Date(utc);
    const gotKey = dateKeyInTimeZone(got, timeZone);
    const gotMin = minutesInTimeZone(got, timeZone);
    const [gy, gm, gd] = gotKey.split('-').map(Number);
    const dayDelta = Date.UTC(year, month - 1, day) - Date.UTC(gy, gm - 1, gd);
    const minDelta = hour * 60 + minute - gotMin;
    if (dayDelta === 0 && minDelta === 0) break;
    utc += dayDelta + minDelta * 60 * 1000;
  }
  return new Date(utc);
}

/** Null when the pickup is allowed. Missing hours means no restriction (legacy). */
export function pickupRejectionReason(
  pickupMs: number,
  hours: BusinessHours | null | undefined
): string | null {
  if (!Number.isFinite(pickupMs)) return 'Invalid pickup time.';
  if (!hours?.days?.length) return null;
  const timeZone = hours.timezone?.trim() || 'Pacific/Auckland';
  const when = new Date(pickupMs);
  const weekday = weekdayInTimeZone(when, timeZone);
  const day = hours.days.find((row) => row.day === weekday);
  const periods = day ? resolveDayPeriods(day) : [];
  if (periods.length === 0) return 'Pickup time is outside restaurant hours.';
  const mins = minutesInTimeZone(when, timeZone);
  if (!isMinutesInPeriods(mins, periods, true)) return 'Pickup time is outside restaurant hours.';
  return null;
}

export function uberMenuServiceAvailability(
  hours: BusinessHours | null | undefined
): { day_of_week: Weekday; time_periods: { start_time: string; end_time: string }[] }[] {
  if (!hours?.days?.length) {
    return WEEKDAY_ORDER.map((day) => ({
      day_of_week: day,
      time_periods: [{ start_time: '00:00', end_time: '23:59' }],
    }));
  }
  const rows: { day_of_week: Weekday; time_periods: { start_time: string; end_time: string }[] }[] = [];
  for (const day of WEEKDAY_ORDER) {
    const config = hours.days.find((row) => row.day === day);
    const periods = config ? resolveDayPeriods(config) : [];
    if (periods.length === 0) continue;
    rows.push({
      day_of_week: day,
      time_periods: periods.map((period) => ({
        start_time: formatMinutesAsHHmm(parseTimeToMinutes(period.open) ?? 0),
        end_time: formatMinutesAsHHmm(parseTimeToMinutes(period.close) ?? 0),
      })),
    });
  }
  return rows;
}

/** Chart buckets from the earliest open hour through the latest close hour. */
export function salesChartHourRange(
  hours: BusinessHours | null | undefined,
  dateKey?: string
): { openingHour: number; closingHour: number } {
  const fallback = { openingHour: 11, closingHour: 22 };
  const days = hours?.days ?? [];
  const collect = (list: BusinessHoursDay[]) => {
    let minOpen = Infinity;
    let maxClose = -Infinity;
    for (const day of list) {
      for (const period of resolveDayPeriods(day)) {
        const open = parseTimeToMinutes(period.open);
        const close = parseTimeToMinutes(period.close);
        if (open == null || close == null || close <= open) continue;
        if (open < minOpen) minOpen = open;
        if (close > maxClose) maxClose = close;
      }
    }
    if (!Number.isFinite(minOpen) || maxClose < 0) return null;
    const openingHour = Math.floor(minOpen / 60);
    const closingHour = Math.min(23, Math.floor(maxClose / 60));
    return { openingHour, closingHour: Math.max(openingHour, closingHour) };
  };
  if (dateKey) {
    const weekday = weekdayFromDateKey(dateKey);
    const specific = weekday ? collect(days.filter((day) => day.day === weekday)) : null;
    if (specific) return specific;
  }
  return collect(days) ?? fallback;
}
